/** @jsx jsx */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  ReactNode,
} from 'react';
import { Link } from '@zapier/common-components';
import { jsx } from '@emotion/core';
import {
  addParamsToUrl,
  extractParams,
  removeParamsFromUrl,
} from '@zapier/url-utils';
import { SSOStartUrlParamsContextType } from '@zapier/auth/src/types';

export const SSOStartUrlParamsContext = createContext<
  SSOStartUrlParamsContextType
>({
  build_auths: false,
});

export type SSOButtonProps = {
  children: ReactNode;
  label?: string;
  href: string;
  next?: string;
  onTrackSignupConversion?: (customUserId: string) => void | Promise<void>;
  onTrackSignupIntent?: () => void;
  passThrough?: boolean;
  shouldOpenInChildWindow?: boolean;
};

const SSOButton = ({
  children,
  href,
  next,
  shouldOpenInChildWindow,
  passThrough,
  onTrackSignupIntent,
  onTrackSignupConversion,
}: SSOButtonProps) => {
  const ssoStartUrlParams = useContext(SSOStartUrlParamsContext);
  const ssoStartUrl = addParamsToUrl(href, {
    ...ssoStartUrlParams,
    in_child_window: shouldOpenInChildWindow,
  });
  const onAuthComplete = useCallback(
    async (event: MessageEvent) => {
      let data = {};

      try {
        data = JSON.parse(event.data as string);

        if (typeof data === 'string') {
          data = JSON.parse(data);
        }
      } catch (e) {
        return;
      }

      const { nextUrl } = data as { nextUrl: string };

      if (!nextUrl) {
        console.error('Error deciphering next URL from posted window message.');
        return;
      }

      const params = extractParams(nextUrl) as {
        new_signup: string;
        customuser_id: string;
      };

      if (params.new_signup && onTrackSignupConversion) {
        try {
          await onTrackSignupConversion(params.customuser_id);
        } catch (e) {
          console.error(e);
        }
      }

      const redirectUrl = removeParamsFromUrl(nextUrl, ['customuser_id']);
      window.location = redirectUrl as any;
    },
    [onTrackSignupConversion]
  );
  const onMessage = useCallback(
    (event: MessageEvent) => {
      // TODO: ignore all other messages but complete
      onAuthComplete(event);
    },
    [onAuthComplete]
  );
  useEffect(() => {
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);
  const onClick = useCallback(() => {
    if (passThrough && next) {
      window.location.href = next;
    }

    if (onTrackSignupIntent) {
      onTrackSignupIntent();
    }

    if (shouldOpenInChildWindow) {
      window.addEventListener('message', onMessage);
      window.open(
        ssoStartUrl,
        'popupwindow',
        'height=600,width=980,resizable=yes,scrollbars=yes'
      );
    } else {
      window.location.href = ssoStartUrl as any;
    }
  }, [
    next,
    onMessage,
    onTrackSignupIntent,
    passThrough,
    shouldOpenInChildWindow,
    ssoStartUrl,
  ]);
  return (
    <Link onClick={onClick} underline="never">
      {children}
    </Link>
  );
};

SSOButton.defaultProps = {
  passThrough: false,
  shouldOpenInChildWindow: true,

  onTrackSignupIntent() {},

  onTrackSignupConversion() {},
};
export default SSOButton;
